import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jobCardsApi } from "@/services/api";

interface OverdueJob {
  id: number;
  jobNo: string;
  title: string;
  client: string;
  dueDate: string;
  progress: number;
  status: string;
  priority: string;
  daysOverdue: number;
}

const priorityConfig: Record<string, { color: string; bg: string; border: string }> = {
  Critical: { color: "#ef4444", bg: "#fef2f2",  border: "#fecaca" },
  High:     { color: "#d97706", bg: "#fffbeb",  border: "#fde68a" },
  Medium:   { color: "#b45309", bg: "#fef3c7",  border: "#fcd34d" },
  Low:      { color: "#92400e", bg: "#fef9ee",  border: "#fde68a" },
};

function getDaysOverdue(dueDate: string): number {
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
}

export default function OverdueJobAlerts() {
  const navigate = useNavigate();
  const [overdueJobs, setOverdueJobs] = useState<OverdueJob[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchOverdue = async () => {
      try {
        const result = await jobCardsApi.getAll({ limit: 200 });
        const list = result?.data || result || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const overdue = (Array.isArray(list) ? list : [])
          .filter((j: Record<string, unknown>) => {
            const due = new Date((j.due_date as string) || (j.dueDate as string));
            due.setHours(0, 0, 0, 0);
            const status = (j.status as string) || "";
            return (
              due < today &&
              status !== "delivered" && status !== "completed" &&
              status !== "Delivered" && status !== "Completed"
            );
          })
          .map((j: Record<string, unknown>) => ({
            id: j.id as number,
            jobNo: (j.job_no || j.jobNo) as string,
            title: j.title as string,
            client: (j.client_name || j.client) as string,
            dueDate: (j.due_date || j.dueDate) as string,
            progress: (j.progress || 0) as number,
            status: j.status as string,
            priority: (j.priority || "Medium") as string,
            daysOverdue: getDaysOverdue((j.due_date || j.dueDate) as string),
          }))
          .sort((a: OverdueJob, b: OverdueJob) => {
            const pOrder: Record<string, number> = { critical: 0, Critical: 0, high: 1, High: 1, medium: 2, Medium: 2, low: 3, Low: 3 };
            const pDiff = (pOrder[a.priority] ?? 4) - (pOrder[b.priority] ?? 4);
            return pDiff !== 0 ? pDiff : b.daysOverdue - a.daysOverdue;
          });
        setOverdueJobs(overdue);
      } catch {
        setOverdueJobs([]);
      }
    };
    fetchOverdue();
  }, []);

  if (overdueJobs.length === 0) return null;

  const visibleJobs = expanded ? overdueJobs : overdueJobs.slice(0, 3);
  const criticalCount = overdueJobs.filter((j) => j.priority === "Critical").length;
  const highCount = overdueJobs.filter((j) => j.priority === "High").length;

  return (
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid #fde68a" }}>
      {/* Header — amber-tinted warning banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-5 py-4"
        style={{ background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", borderBottom: "1px solid #fde68a" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: "#fde68a" }}>
            <i className="ri-error-warning-line text-lg" style={{ color: "#d97706" }} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-900 text-sm">Overdue Job Cards</h3>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "#d97706" }}>
                {overdueJobs.length}
              </span>
            </div>
            <p className="text-xs mt-0.5" style={{ color: "#92400e" }}>
              {criticalCount > 0 && <span className="font-semibold text-red-600">{criticalCount} Critical</span>}
              {criticalCount > 0 && highCount > 0 && <span className="mx-1">·</span>}
              {highCount > 0 && <span>{highCount} High priority</span>}
              {criticalCount === 0 && highCount === 0 && "Immediate attention required"}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate("/job-cards?filter=overdue")}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all self-start sm:self-auto text-white"
          style={{ background: "#d97706" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#b45309"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "#d97706"; }}
        >
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-external-link-line text-xs" />
          </span>
          View All Overdue
        </button>
      </div>

      {/* Job list */}
      <div className="divide-y" style={{ borderColor: "#fef3c7" }}>
        {visibleJobs.map((job) => {
          const cfg = priorityConfig[job.priority] || priorityConfig.Medium;
          return (
            <div
              key={job.id}
              onClick={() => navigate(`/job-cards/${job.id}`)}
              className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 cursor-pointer transition-all group"
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#fffbeb"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}
            >
              {/* Priority bar */}
              <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ background: cfg.color }} />

              {/* Job info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span className="text-xs font-bold font-mono text-gray-400">{job.jobNo}</span>
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ color: cfg.color, background: cfg.bg }}>
                    {job.priority}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: "#fef3c7", color: "#92400e" }}>
                    {job.status}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800 truncate">{job.title}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{job.client}</p>
              </div>

              {/* Progress — hidden on small screens */}
              <div className="w-20 md:w-24 flex-shrink-0 hidden sm:block">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">Progress</span>
                  <span className="text-xs font-semibold text-gray-600">{job.progress}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#fef3c7" }}>
                  <div className="h-full rounded-full" style={{ width: `${job.progress}%`, background: cfg.color }} />
                </div>
              </div>

              {/* Overdue badge */}
              <div className="flex-shrink-0 text-right">
                <div
                  className="inline-flex items-center gap-1 px-2 md:px-2.5 py-1 rounded-lg text-xs font-bold"
                  style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
                >
                  <i className="ri-time-line text-xs" />
                  <span className="hidden sm:inline">
                    {job.daysOverdue === 0 ? "Due today" : job.daysOverdue === 1 ? "1 day" : `${job.daysOverdue}d`}
                  </span>
                  <span className="sm:hidden">
                    {job.daysOverdue === 0 ? "Today" : `${job.daysOverdue}d`}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 hidden sm:block">
                  Due {new Date(job.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </p>
              </div>

              {/* Arrow */}
              <div className="w-5 h-5 flex items-center justify-center text-gray-300 group-hover:text-amber-500 transition-colors flex-shrink-0">
                <i className="ri-arrow-right-s-line text-base" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 md:px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
        style={{ borderTop: "1px solid #fef3c7", background: "#fffbeb" }}
      >
        {overdueJobs.length > 3 && (
          <p className="text-xs text-gray-500">
            Showing <strong>{visibleJobs.length}</strong> of <strong>{overdueJobs.length}</strong> overdue jobs
          </p>
        )}
        <div className="flex items-center gap-3 ml-auto">
          {overdueJobs.length > 3 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer transition-colors"
            >
              {expanded ? "Show less ↑" : `Show all ${overdueJobs.length} ↓`}
            </button>
          )}
          <button
            onClick={() => navigate("/job-cards?filter=overdue")}
            className="text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
            style={{ color: "#d97706" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#b45309"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "#d97706"; }}
          >
            Open in Job Cards →
          </button>
        </div>
      </div>
    </div>
  );
}
